import { useState, useEffect, useCallback, useMemo } from 'react';
import { apiClient } from '../../api/client';
import { PRIMARY, TEXT_DARK, TEXT_MUTED, BORDER_LIGHT, BG_CARD, BG_SURFACE } from './table-management/designTokens';

// ─── Types ────────────────────────────────────────────────────────────────────

interface RequestMember {
  id: string;
  guestId: string | null;
  freeformName: string | null;
  guest: {
    id: string;
    fullName: string;
    ticketNumber: string | null;
    chapterAffiliation: string | null;
    familyAssociation: string | null;
    orderId: string | null;
    dietaryPreference: string | null;
    seat?: Array<{ seatNumber?: number; seatLabel?: string; table: { tableName: string; tableNumber: number; capacity: number; zone: { name: string }; _count: { seats: number } } }>;
  } | null;
}

interface ReservationRequest {
  id: string;
  name: string;
  status: string;
  rejectionReason: string | null;
  createdAt: string;
  createdBy: { id: string; fullName: string };
  members: RequestMember[];
  _count: { members: number };
}

interface GuestSearchResult {
  id: string;
  fullName: string;
  ticketNumber: string | null;
  familyAssociation?: string | null;
}

// ─── Status Colors ────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  Requested: { bg: '#e3f2fd', color: '#1565c0' },
  InProgress: { bg: '#fff3e0', color: '#e65100' },
  GroupCreated: { bg: '#e8f5e9', color: '#2e7d32' },
  Fulfilled: { bg: '#e8f5e9', color: '#1b5e20' },
  Rejected: { bg: '#ffebee', color: '#c62828' },
  Inactive: { bg: '#f3f4f6', color: '#6b7280' },
};

// ─── AI Insights Generator ───────────────────────────────────────────────────

function generateAIInsights(detail: ReservationRequest): string[] {
  const insights: string[] = [];
  const members = detail.members;
  const linkedCount = members.filter((m) => m.guestId).length;
  const freeformCount = members.filter((m) => !m.guestId).length;
  const eligibleCount = members.filter((m) => m.guestId && !m.guest?.familyAssociation).length;
  const seatedCount = members.filter((m) => m.guest?.seat && m.guest.seat.length > 0).length;
  const chapters = [...new Set(members.filter((m) => m.guest?.chapterAffiliation).map((m) => m.guest!.chapterAffiliation!))];
  const families = [...new Set(members.filter((m) => m.guest?.familyAssociation).map((m) => m.guest!.familyAssociation!))];
  const orderIds = [...new Set(members.filter((m) => m.guest?.orderId).map((m) => m.guest!.orderId!))];
  const zones = [...new Set(members.filter((m) => m.guest?.seat?.[0]).map((m) => m.guest!.seat![0].table.zone.name))];
  const dietaryPrefs = members.filter((m) => m.guest?.dietaryPreference).map((m) => m.guest!.dietaryPreference!);

  // Summary
  insights.push(`Group has ${members.length} member${members.length !== 1 ? 's' : ''}: ${linkedCount} linked, ${freeformCount} unlinked.`);

  // Orders
  if (orderIds.length > 0) {
    insights.push(`Members come from ${orderIds.length} distinct order${orderIds.length !== 1 ? 's' : ''}.`);
    if (orderIds.length === 1) {
      insights.push('All linked members are from the same ticket order — strong connection.');
    }
  }

  // Multi-chapter detection
  if (chapters.length > 1) {
    insights.push(`⚠️ Cross-chapter request: members from ${chapters.length} chapters (${chapters.join(', ')}).`);
  } else if (chapters.length === 1) {
    insights.push(`All members are from the ${chapters[0]} chapter.`);
  }

  // Family eligibility
  if (eligibleCount > 0) {
    insights.push(`${eligibleCount} member${eligibleCount !== 1 ? 's are' : ' is'} eligible for family group creation.`);
  } else if (linkedCount > 0 && families.length > 0) {
    insights.push('🚫 All linked members already belong to a family group.');
  }

  // Existing family groups
  if (families.length > 1) {
    insights.push(`Members span ${families.length} existing family groups: ${families.join(', ')}.`);
  } else if (families.length === 1) {
    insights.push(`Existing family association: "${families[0]}".`);
  }

  // Seating status
  if (seatedCount > 0) {
    const tables = [...new Set(members.filter((m) => m.guest?.seat?.[0]).map((m) => m.guest!.seat![0].table.tableName))];
    if (tables.length === 1) {
      insights.push(`All ${seatedCount} seated member${seatedCount !== 1 ? 's' : ''} already at ${tables[0]} — request may be fulfilled.`);
    } else {
      insights.push(`${seatedCount} member${seatedCount !== 1 ? 's' : ''} seated across ${tables.length} tables: ${tables.join(', ')}.`);
    }
    if (zones.length > 1) {
      insights.push(`Seated in multiple zones (${zones.join(', ')}) — relocation may be needed.`);
    }
    // Table availability details
    const tableDetails = new Map<string, { capacity: number; occupied: number }>();
    for (const m of members) {
      const seat = m.guest?.seat?.[0];
      if (seat) {
        const tName = seat.table.tableName;
        if (!tableDetails.has(tName)) {
          tableDetails.set(tName, { capacity: seat.table.capacity, occupied: seat.table._count?.seats || 0 });
        }
      }
    }
    const availInfo = [...tableDetails.entries()].map(([name, info]) => {
      const available = info.capacity - info.occupied;
      return `${name}: ${available}/${info.capacity} available`;
    });
    if (availInfo.length > 0) {
      insights.push(`📊 Table availability: ${availInfo.join(' | ')}.`);
    }
  } else {
    insights.push('No members currently seated — full flexibility for table assignment.');
  }

  // Dietary considerations
  if (dietaryPrefs.length > 0) {
    const uniqueDiets = [...new Set(dietaryPrefs)];
    if (uniqueDiets.length > 0) {
      insights.push(`Dietary needs in group: ${uniqueDiets.join(', ')}.`);
    }
  }

  // Group size assessment
  if (members.length >= 10) {
    insights.push('Large group (10+) — may need a dedicated table or adjacent tables.');
  } else if (members.length >= 6) {
    insights.push('Medium group — will fill most of a standard table (10 seats).');
  }

  // Recommendations
  if (detail.status === 'Requested' && eligibleCount >= 2) {
    insights.push('💡 Recommendation: Create a family group to prioritize togetherness during batch seating.');
  }
  if (detail.status === 'InProgress' && seatedCount === 0 && linkedCount > 0) {
    insights.push('💡 Next step: Create family group, then run batch seating to place them together.');
  }
  if (detail.status === 'InProgress' && detail.name.match(/RES-/i) && families.length === 0 && linkedCount > 0) {
    insights.push('⚠️ Family group was deleted externally. Status reverted to In Progress — you can recreate it.');
  }
  if (freeformCount > 0) {
    insights.push(`⚠️ ${freeformCount} name${freeformCount !== 1 ? 's are' : ' is'} not linked to a guest record — they won't be included in family group creation.`);
  }

  return insights;
}

// ─── Responsive Hook ─────────────────────────────────────────────────────────

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function TableReservationRequests() {
  const isMobile = useIsMobile();
  const [requests, setRequests] = useState<ReservationRequest[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detailData, setDetailData] = useState<ReservationRequest | null>(null);
  const [sortByName, setSortByName] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Mobile pane navigation
  const [mobilePane, setMobilePane] = useState<'list' | 'detail' | 'actions'>('list');

  // Create modal state
  const [showCreate, setShowCreate] = useState(false);
  const [createName, setCreateName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<GuestSearchResult[]>([]);
  const [pendingMembers, setPendingMembers] = useState<Array<{ guestId?: string; freeformName?: string; displayName: string; familyGroup?: string | null }>>([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);

  // Status update
  const [rejectReason, setRejectReason] = useState('');

  // Inline member management
  const [addMemberQuery, setAddMemberQuery] = useState('');
  const [addMemberResults, setAddMemberResults] = useState<GuestSearchResult[]>([]);
  const [addMemberSearching, setAddMemberSearching] = useState(false);
  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null);
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [seatingGroup, setSeatingGroup] = useState(false);
  const [seatingResult, setSeatingResult] = useState<string | null>(null);
  const [showSeatModal, setShowSeatModal] = useState(false);
  const [seatZoneId, setSeatZoneId] = useState<string | null>(null);
  const [seatRule, setSeatRule] = useState<'keep_together' | 'allow_split' | 'start_new_table'>('keep_together');
  const [zones, setZones] = useState<Array<{ id: string; name: string }>>([]);
  const [editingName, setEditingName] = useState(false);
  const [editNameValue, setEditNameValue] = useState('');

  const fetchRequests = useCallback(async () => {
    try {
      const data = await apiClient<ReservationRequest[]>('/table-reservation-requests');
      setRequests(data);
    } catch { setError('Failed to load requests.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  useEffect(() => {
    if (!selectedId) { setDetailData(null); return; }
    apiClient<ReservationRequest>(`/table-reservation-requests/${selectedId}`)
      .then(setDetailData)
      .catch(() => setDetailData(null));
  }, [selectedId]);

  // Search guests (create modal)
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) { setSearchResults([]); return; }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const data = await apiClient<any>(`/table-management/guests?query=${encodeURIComponent(searchQuery)}&limit=10`);
        const guests = Array.isArray(data) ? data : (data.data || data.guests || []);
        const pendingIds = new Set(pendingMembers.filter((m) => m.guestId).map((m) => m.guestId));
        setSearchResults(guests.filter((g: GuestSearchResult) => !pendingIds.has(g.id)));
      } catch { setSearchResults([]); }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, pendingMembers]);

  // Inline Add Member search
  useEffect(() => {
    if (!addMemberQuery || addMemberQuery.length < 2) { setAddMemberResults([]); return; }
    const timer = setTimeout(async () => {
      setAddMemberSearching(true);
      try {
        const data = await apiClient<any>(`/table-management/guests?query=${encodeURIComponent(addMemberQuery)}&limit=10`);
        const guests = Array.isArray(data) ? data : (data.data || data.guests || []);
        const existingIds = new Set(detailData?.members.filter((m) => m.guestId).map((m) => m.guestId) || []);
        setAddMemberResults(guests.filter((g: GuestSearchResult) => !existingIds.has(g.id)));
      } catch { setAddMemberResults([]); }
      finally { setAddMemberSearching(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [addMemberQuery, detailData]);

  // Fetch zones for seat modal
  useEffect(() => {
    apiClient<Array<{ id: string; name: string }>>('/table-management/zones')
      .then(setZones)
      .catch(() => setZones([]));
  }, []);

  const handleCreate = async () => {
    if (!createName.trim() || pendingMembers.length === 0) return;
    setSaving(true);
    try {
      await apiClient('/table-reservation-requests', {
        method: 'POST',
        body: { name: createName.trim(), members: pendingMembers.map((m) => ({ guestId: m.guestId, freeformName: m.freeformName })) },
      });
      setShowCreate(false);
      setCreateName('');
      setPendingMembers([]);
      await fetchRequests();
    } catch (err: any) { setError(err?.message || 'Failed to create request.'); }
    finally { setSaving(false); }
  };

  const handleStatusChange = async (status: string) => {
    if (!selectedId) return;
    try {
      await apiClient(`/table-reservation-requests/${selectedId}/status`, {
        method: 'PATCH',
        body: { status, rejectionReason: status === 'Rejected' ? rejectReason : undefined },
      });
      setRejectReason('');
      await fetchRequests();
      const updated = await apiClient<ReservationRequest>(`/table-reservation-requests/${selectedId}`);
      setDetailData(updated);
    } catch (err: any) { setError(err?.message || 'Failed to update status.'); }
  };

  const handleAddMember = async (guest: GuestSearchResult) => {
    if (!selectedId) return;
    try {
      await apiClient(`/table-reservation-requests/${selectedId}/members`, {
        method: 'POST',
        body: { guestId: guest.id },
      });
      setAddMemberResults((prev) => prev.filter((g) => g.id !== guest.id));
      const updated = await apiClient<ReservationRequest>(`/table-reservation-requests/${selectedId}`);
      setDetailData(updated);
      await fetchRequests();
    } catch (err: any) { setError(err?.message || 'Failed to add member.'); }
  };

  const handleAddFreeformMember = async (name: string) => {
    if (!selectedId) return;
    try {
      await apiClient(`/table-reservation-requests/${selectedId}/members`, {
        method: 'POST',
        body: { freeformName: name },
      });
      setAddMemberQuery('');
      setAddMemberResults([]);
      const updated = await apiClient<ReservationRequest>(`/table-reservation-requests/${selectedId}`);
      setDetailData(updated);
      await fetchRequests();
    } catch (err: any) { setError(err?.message || 'Failed to add member.'); }
  };

  const handleRemoveMember = async (memberId: string) => {
    if (!selectedId) return;
    setRemovingMemberId(memberId);
    try {
      await apiClient(`/table-reservation-requests/${selectedId}/members/${memberId}`, { method: 'DELETE' });
      const updated = await apiClient<ReservationRequest>(`/table-reservation-requests/${selectedId}`);
      setDetailData(updated);
      await fetchRequests();
    } catch (err: any) { setError(err?.message || 'Failed to remove member.'); }
    finally { setRemovingMemberId(null); }
  };

  const handleCreateFamilyGroup = async () => {
    if (!selectedId) return;
    setCreatingGroup(true);
    try {
      await apiClient(`/table-reservation-requests/${selectedId}/create-family-group`, { method: 'POST' });
      const updated = await apiClient<ReservationRequest>(`/table-reservation-requests/${selectedId}`);
      setDetailData(updated);
      await fetchRequests();
    } catch (err: any) { setError(err?.message || 'Failed to create family group.'); }
    finally { setCreatingGroup(false); }
  };

  const handleMakeInactive = async () => {
    if (!selectedId) return;
    try {
      await apiClient(`/table-reservation-requests/${selectedId}/status`, {
        method: 'PATCH',
        body: { status: 'Inactive' },
      });
      await fetchRequests();
      const updated = await apiClient<ReservationRequest>(`/table-reservation-requests/${selectedId}`);
      setDetailData(updated);
    } catch (err: any) { setError(err?.message || 'Failed to make inactive.'); }
  };

  const handleRename = async () => {
    if (!selectedId || !editNameValue.trim()) return;
    try {
      await apiClient(`/table-reservation-requests/${selectedId}/name`, {
        method: 'PATCH',
        body: { name: editNameValue.trim() },
      });
      setEditingName(false);
      await fetchRequests();
      const updated = await apiClient<ReservationRequest>(`/table-reservation-requests/${selectedId}`);
      setDetailData(updated);
    } catch (err: any) { setError(err?.message || 'Failed to rename.'); }
  };

  // ─── Print Table Request Card (5x7 card format) ────────────────────────────
  const handlePrintTableRequestCard = (request: ReservationRequest) => {
    const requestName = request.name;
    // Sort members by table number then name
    const sortedMembers = [...request.members].sort((a, b) => {
      const tableA = a.guest?.seat?.[0]?.table?.tableNumber ?? 9999;
      const tableB = b.guest?.seat?.[0]?.table?.tableNumber ?? 9999;
      if (tableA !== tableB) return tableA - tableB;
      const nameA = a.guest?.fullName || a.freeformName || '';
      const nameB = b.guest?.fullName || b.freeformName || '';
      return nameA.localeCompare(nameB);
    });
    // Determine table name from first seated member
    const firstSeated = request.members.find((m) => m.guest?.seat?.[0]);
    const tableName = firstSeated?.guest?.seat?.[0]?.table?.tableName || 'TBD';
    // Split table name at first dash for two-line display
    const dashIdx = tableName.indexOf('-');
    const tableNameLine1 = dashIdx > 0 ? tableName.substring(0, dashIdx).trim() : tableName;
    const tableNameLine2 = dashIdx > 0 ? tableName.substring(dashIdx + 1).trim() : '';

    // Paginate: max 10 names per page
    const PAGE_SIZE = 10;
    const pages: typeof sortedMembers[] = [];
    for (let i = 0; i < sortedMembers.length; i += PAGE_SIZE) {
      pages.push(sortedMembers.slice(i, i + PAGE_SIZE));
    }

    const buildPageHtml = (pageMembers: typeof sortedMembers, pageNum: number, totalPages: number) => {
      const rows = pageMembers.map((m) => {
        const name = m.guest?.fullName || m.freeformName || '—';
        const ticket = m.guest?.ticketNumber || '—';
        const seat = m.guest?.seat?.[0];
        const seatLabel = seat ? (seat.seatLabel || String(seat.seatNumber) || '—') : '—';
        return `<tr><td style="padding:5px 8px;border-bottom:1px solid #e0e0e0;font-size:11px;">${name}</td><td style="padding:5px 8px;border-bottom:1px solid #e0e0e0;font-size:11px;text-align:center;">${ticket}</td><td style="padding:5px 8px;border-bottom:1px solid #e0e0e0;font-size:11px;text-align:center;">${seatLabel}</td></tr>`;
      }).join('');

      const pageIndicator = totalPages > 1 ? `<div style="font-size:8px;color:#9ca3af;margin-top:4px;">Page ${pageNum} of ${totalPages}</div>` : '';

      return `<div class="card">
        <img src="/KDULogo.PNG" class="logo" alt="KDU Logo" />
        <div class="header">${requestName}</div>
        <table class="guest-table">
          <thead><tr><th>Name</th><th>Ticket #</th><th>Seat #</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>
        <div class="table-pill">
          <span class="table-pill-line1">${tableNameLine1}</span>
          ${tableNameLine2 ? `<span class="table-pill-line2">${tableNameLine2}</span>` : ''}
        </div>
        <div class="footer">KDU Georgia 2026 Legacy Ball${pageIndicator ? ' &nbsp;•&nbsp; ' : ''}${totalPages > 1 ? `Page ${pageNum}/${totalPages}` : ''}</div>
      </div>`;
    };

    const cardsHtml = pages.map((page, idx) => buildPageHtml(page, idx + 1, pages.length)).join('\n');

    const html = `<!DOCTYPE html><html><head>
      <title>Table Request - ${requestName}</title>
      <style>
        @page { size: 5in 7in; margin: 0; }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Segoe UI', system-ui, sans-serif; }
        .card { width: 5in; height: 7in; display: flex; flex-direction: column; align-items: center; justify-content: flex-start; padding: 0.25in; page-break-after: always; }
        .card:last-child { page-break-after: auto; }
        .card > * { max-width: 4.5in; }
        .card::before { content: ''; position: absolute; top: 0.25in; left: 0.25in; right: 0.25in; bottom: 0.25in; border: 2px solid #333; border-radius: 12px; pointer-events: none; }
        .card { position: relative; }
        .logo { width: 70px; height: auto; margin-top: 0.15in; margin-bottom: 6px; }
        .header { font-size: 22px; font-weight: 800; color: #0f2d1f; text-align: center; margin-bottom: 10px; text-transform: uppercase; letter-spacing: 0.5px; }
        .guest-table { width: 4in; border-collapse: collapse; margin-bottom: auto; }
        .guest-table th { text-align: left; padding: 5px 8px; font-size: 10px; font-weight: 700; color: #6b7280; text-transform: uppercase; border-bottom: 2px solid #333; }
        .guest-table th:nth-child(2), .guest-table th:nth-child(3) { text-align: center; }
        .table-pill { margin-top: 14px; display: inline-flex; flex-direction: column; align-items: center; justify-content: center; background-color: #1a6b3c; color: #fff; font-weight: 800; padding: 18px 36px; border-radius: 60px; min-width: 160px; }
        .table-pill-line1 { font-size: 20px; line-height: 1.3; }
        .table-pill-line2 { font-size: 16px; line-height: 1.3; opacity: 0.9; }
        .footer { margin-top: 8px; font-size: 8px; color: #9ca3af; margin-bottom: 0.1in; }
      </style>
    </head><body>${cardsHtml}</body></html>`;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => printWindow.print(), 300);
    }
  };

  // ─── Share Table Request guest list to WhatsApp ────────────────────────────
  const handleShareToWhatsApp = (request: ReservationRequest) => {
    const lines: string[] = [];
    lines.push(`🪑 *${request.name}* — Table Request`);
    lines.push(`Status: ${request.status}${request._count?.members != null ? ` · ${request._count.members} guest${request._count.members !== 1 ? 's' : ''}` : ''}`);
    lines.push('');
    // Sort by table number (seated first), then name
    const sorted = [...request.members].sort((a, b) => {
      const ta = a.guest?.seat?.[0]?.table.tableNumber ?? 9999;
      const tb = b.guest?.seat?.[0]?.table.tableNumber ?? 9999;
      if (ta !== tb) return ta - tb;
      const na = a.guest?.fullName || a.freeformName || '';
      const nb = b.guest?.fullName || b.freeformName || '';
      return na.localeCompare(nb);
    });
    sorted.forEach((m, i) => {
      const name = m.guest?.fullName || m.freeformName || '—';
      const seat = m.guest?.seat?.[0];
      const seatText = seat ? ` — ${seat.table.tableName || `Table ${seat.table.tableNumber}`}${seat.seatNumber != null ? `, Seat ${seat.seatNumber}` : ''}` : '';
      const ticket = m.guest?.ticketNumber ? ` (${m.guest.ticketNumber})` : '';
      lines.push(`${i + 1}. ${name}${ticket}${seatText}`);
    });
    lines.push('');
    lines.push('— KDU Georgia 2026');
    const text = lines.join('\n');
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const handleSeatFamilyGroup = async () => {
    if (!detailData) return;
    const familyName = detailData.members.find((m) => m.guest?.familyAssociation)?.guest?.familyAssociation;
    if (!familyName) { setError('No family group found on members.'); return; }
    setSeatingGroup(true);
    setSeatingResult(null);
    try {
      const groups = await apiClient<Array<{ id: string; name: string }>>('/batch-assignment/family-groups');
      const group = groups.find((g) => g.name === familyName);
      if (!group) { setError(`Family group "${familyName}" not found.`); setSeatingGroup(false); return; }
      const result = await apiClient<any>('/batch-assignment/family-groups/seat', {
        method: 'POST',
        body: { groupIds: [group.id], zoneId: seatZoneId, rule: seatRule },
      });
      const seated = result.seatedCount || result.results?.[0]?.seatedCount || 0;
      setSeatingResult(`Seated ${seated} member${seated !== 1 ? 's' : ''} from "${familyName}" (${seatRule.replace(/_/g, ' ')}).`);
      setShowSeatModal(false);
      const updated = await apiClient<ReservationRequest>(`/table-reservation-requests/${selectedId}`);
      setDetailData(updated);
      await fetchRequests();
    } catch (err: any) { setError(err?.message || 'Failed to seat family group.'); }
    finally { setSeatingGroup(false); }
  };

  const handleDelete = async () => {
    if (!selectedId || !window.confirm('Delete this request?')) return;
    try {
      await apiClient(`/table-reservation-requests/${selectedId}`, { method: 'DELETE' });
      setSelectedId(null);
      setDetailData(null);
      await fetchRequests();
    } catch (err: any) { setError(err?.message || 'Failed to delete.'); }
  };

  const aiInsights = useMemo(() => detailData ? generateAIInsights(detailData) : [], [detailData]);

  if (loading) return <div style={{ padding: '2rem', color: TEXT_MUTED, fontSize: '0.85rem' }}>Loading table requests...</div>;

  // ─── Pane 1: Request List ───────────────────────────────────────────────────
  const sortedRequests = sortByName
    ? [...requests].sort((a, b) => a.name.localeCompare(b.name))
    : requests;

  const renderRequestList = () => (
    <div style={{ maxHeight: isMobile ? 'none' : '500px', overflowY: 'auto' }}>
      <div style={{ padding: '0.5rem 0.6rem', backgroundColor: BG_SURFACE, borderBottom: `1px solid ${BORDER_LIGHT}`, fontSize: '0.72rem', fontWeight: 700, color: TEXT_MUTED, textTransform: 'uppercase', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Requests</span>
        <button
          onClick={() => setSortByName(!sortByName)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.68rem', color: sortByName ? PRIMARY : TEXT_MUTED, fontWeight: 600, padding: '1px 4px' }}
          title={sortByName ? 'Sorted A-Z by name. Click for default order.' : 'Sort alphabetically by name'}
          aria-label="Toggle sort by name"
        >
          {sortByName ? '🔤 A-Z' : '🔤'}
        </button>
      </div>
      {sortedRequests.length === 0 && <div style={{ padding: '1rem', fontSize: '0.78rem', color: TEXT_MUTED, textAlign: 'center' }}>No requests yet.</div>}
      {sortedRequests.map((r) => {
        const colors = STATUS_COLORS[r.status] || STATUS_COLORS.Requested;
        return (
          <div key={r.id} onClick={() => { setSelectedId(r.id); if (isMobile) setMobilePane('detail'); }} style={{ padding: '0.5rem 0.6rem', cursor: 'pointer', backgroundColor: selectedId === r.id ? '#ffffff' : r.status === 'Fulfilled' ? '#f3f4f6' : BG_CARD, borderBottom: `1px solid ${BORDER_LIGHT}`, borderLeft: selectedId === r.id ? `3px solid ${PRIMARY}` : '3px solid transparent', boxShadow: selectedId === r.id ? '0 1px 4px rgba(0,0,0,0.08)' : 'none', opacity: r.status === 'Fulfilled' ? 0.7 : 1 }}>
            <div style={{ fontSize: '0.82rem', fontWeight: 600, color: TEXT_DARK }}>{r.name}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '0.2rem' }}>
              <span style={{ fontSize: '0.65rem', padding: '1px 6px', borderRadius: '8px', backgroundColor: colors.bg, color: colors.color, fontWeight: 600 }}>{r.status}</span>
              <span style={{ fontSize: '0.68rem', color: TEXT_MUTED }}>{r._count.members} member{r._count.members !== 1 ? 's' : ''}</span>
            </div>
          </div>
        );
      })}
    </div>
  );

  // ─── Pane 2: Members ────────────────────────────────────────────────────────
  const renderMembersPane = () => {
    if (!detailData) return <div style={{ padding: '1rem', fontSize: '0.78rem', color: TEXT_MUTED, textAlign: 'center' }}>Select a request</div>;

    // Max 15 rows visible (~27px per row), scroll for more
    const ROW_HEIGHT = 27;
    const MAX_VISIBLE = 15;
    const tableMaxHeight = ROW_HEIGHT * MAX_VISIBLE;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Add Member Search */}
        {detailData.status !== 'Fulfilled' && detailData.status !== 'Inactive' && (
          <div style={{ padding: '0.4rem 0.5rem', borderBottom: `1px solid ${BORDER_LIGHT}`, backgroundColor: '#fafafa', flexShrink: 0 }}>
            <input
              value={addMemberQuery}
              onChange={(e) => setAddMemberQuery(e.target.value)}
              placeholder="+ Search guest to add..."
              style={{ width: '100%', padding: '0.3rem 0.4rem', fontSize: '0.75rem', border: `1px solid ${BORDER_LIGHT}`, borderRadius: '4px', outline: 'none', boxSizing: 'border-box' }}
            />
            {(addMemberSearching || addMemberResults.length > 0) && (
              <div style={{ maxHeight: '120px', overflowY: 'auto', border: `1px solid ${BORDER_LIGHT}`, borderRadius: '4px', marginTop: '0.25rem', backgroundColor: BG_CARD }}>
                {addMemberSearching && <div style={{ padding: '0.4rem', fontSize: '0.7rem', color: TEXT_MUTED, textAlign: 'center' }}>Searching...</div>}
                {addMemberResults.map((g) => (
                  <div key={g.id} onClick={() => handleAddMember(g)} style={{ padding: '0.3rem 0.5rem', fontSize: '0.75rem', cursor: 'pointer', borderBottom: `1px solid ${BORDER_LIGHT}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{g.fullName}</span>
                    <span style={{ fontSize: '0.65rem', color: TEXT_MUTED }}>{g.familyAssociation ? `👨‍👩‍👧‍👦 ${g.familyAssociation}` : g.ticketNumber || ''}</span>
                  </div>
                ))}
              </div>
            )}
            {addMemberQuery.length >= 2 && addMemberResults.length === 0 && !addMemberSearching && (
              <button onClick={() => handleAddFreeformMember(addMemberQuery.trim())} style={{ marginTop: '0.25rem', padding: '0.25rem 0.5rem', fontSize: '0.7rem', backgroundColor: '#fff3e0', color: '#e65100', border: '1px solid #fde68a', borderRadius: '4px', cursor: 'pointer' }}>
                + Add "{addMemberQuery.trim()}" as new name
              </button>
            )}
          </div>
        )}

        {/* Member Table — scrollable, max 15 rows */}
        <div style={{ overflowY: 'auto', maxHeight: `${tableMaxHeight}px`, flexShrink: 0 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: isMobile ? '0.7rem' : '0.75rem' }}>
            <thead>
              <tr style={{ backgroundColor: BG_SURFACE, position: 'sticky', top: 0, zIndex: 1 }}>
                <th style={{ textAlign: 'left', padding: '0.35rem 0.4rem', color: TEXT_MUTED, fontWeight: 600, borderBottom: `1px solid ${BORDER_LIGHT}` }}>Name</th>
                {!isMobile && <th style={{ textAlign: 'left', padding: '0.35rem 0.4rem', color: TEXT_MUTED, fontWeight: 600, borderBottom: `1px solid ${BORDER_LIGHT}` }}>Ticket</th>}
                <th style={{ textAlign: 'left', padding: '0.35rem 0.4rem', color: TEXT_MUTED, fontWeight: 600, borderBottom: `1px solid ${BORDER_LIGHT}` }}>Chapter</th>
                {!isMobile && <th style={{ textAlign: 'left', padding: '0.35rem 0.4rem', color: TEXT_MUTED, fontWeight: 600, borderBottom: `1px solid ${BORDER_LIGHT}` }}>Family</th>}
                <th style={{ textAlign: 'center', padding: '0.35rem 0.3rem', color: TEXT_MUTED, fontWeight: 600, borderBottom: `1px solid ${BORDER_LIGHT}`, cursor: 'help' }} title="Family Group Eligibility: &#10;🟢 = Can be added to a family group&#10;🔵 = Already in a family group&#10;🟠 = Freeform name (not linked to guest record)">Elig.</th>
                <th style={{ textAlign: 'left', padding: '0.35rem 0.4rem', color: TEXT_MUTED, fontWeight: 600, borderBottom: `1px solid ${BORDER_LIGHT}` }}>Table</th>
                <th style={{ textAlign: 'left', padding: '0.35rem 0.3rem', color: TEXT_MUTED, fontWeight: 600, borderBottom: `1px solid ${BORDER_LIGHT}` }}>Seat#</th>
                <th style={{ textAlign: 'left', padding: '0.35rem 0.3rem', color: TEXT_MUTED, fontWeight: 600, borderBottom: `1px solid ${BORDER_LIGHT}` }}>T#</th>
                <th style={{ textAlign: 'center', padding: '0.35rem 0.2rem', color: TEXT_MUTED, fontWeight: 600, borderBottom: `1px solid ${BORDER_LIGHT}`, width: '26px' }}></th>
              </tr>
            </thead>
            <tbody>
              {detailData.members.map((m) => {
                const name = m.guest?.fullName || m.freeformName || '—';
                const seat = m.guest?.seat?.[0];
                const isEligible = !!m.guestId && !m.guest?.familyAssociation;
                return (
                  <tr key={m.id} style={{ borderBottom: `1px solid ${BORDER_LIGHT}` }}>
                    <td style={{ padding: '0.3rem 0.4rem', fontWeight: 500 }}>
                      {name}
                      {!m.guest && <span style={{ fontSize: '0.6rem', marginLeft: '3px', color: '#e65100', backgroundColor: '#fff3e0', padding: '1px 3px', borderRadius: '3px' }}>New</span>}
                    </td>
                    {!isMobile && <td style={{ padding: '0.3rem 0.4rem' }}>{m.guest?.ticketNumber || '—'}</td>}
                    <td style={{ padding: '0.3rem 0.4rem', color: '#1565c0' }}>{m.guest?.chapterAffiliation || '—'}</td>
                    {!isMobile && <td style={{ padding: '0.3rem 0.4rem', color: '#7c3aed' }}>{m.guest?.familyAssociation || '—'}</td>}
                    <td style={{ padding: '0.3rem 0.3rem', textAlign: 'center' }}>
                      {m.guestId ? (isEligible ? <span title="Eligible — can be grouped into a family" style={{ fontSize: '0.7rem' }}>🟢</span> : <span title="Already in a family group" style={{ fontSize: '0.7rem' }}>🔵</span>) : <span title="Freeform name — not linked to a guest record" style={{ fontSize: '0.7rem' }}>🟠</span>}
                    </td>
                    <td style={{ padding: '0.3rem 0.4rem', color: '#166534', fontSize: '0.7rem' }}>{seat ? `${seat.table.tableName}` : '—'}</td>
                    <td style={{ padding: '0.3rem 0.3rem' }}>{seat ? seat.seatLabel || seat.seatNumber || '—' : '—'}</td>
                    <td style={{ padding: '0.3rem 0.3rem' }}>{seat ? seat.table.tableNumber : '—'}</td>
                    <td style={{ padding: '0.3rem 0.2rem', textAlign: 'center' }}>
                      {detailData.status !== 'Fulfilled' && detailData.status !== 'Inactive' && (
                        <button onClick={() => handleRemoveMember(m.id)} disabled={removingMemberId === m.id} title="Remove" aria-label={`Remove ${name}`} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '1px 3px', fontSize: '0.75rem', lineHeight: 1, color: '#d32f2f', opacity: removingMemberId === m.id ? 0.4 : 1 }}>✕</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* AI Insights Panel — bottom of Pane 2 */}
        <div style={{ marginTop: 'auto', borderTop: `1px solid ${BORDER_LIGHT}`, padding: '0.6rem', backgroundColor: '#f0f4ff', flexShrink: 0, minHeight: '140px', maxHeight: '220px', overflowY: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', marginBottom: '0.4rem' }}>
            <span style={{ fontSize: '0.8rem' }}>🤖</span>
            <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#3730a3', textTransform: 'uppercase' }}>AI Insights</span>
          </div>
          {aiInsights.length > 0 ? (
            <ul style={{ margin: 0, paddingLeft: '1rem', listStyle: 'disc' }}>
              {aiInsights.map((insight, i) => {
                const isBoldRed = insight.startsWith('🚫');
                return (
                  <li key={i} style={{ fontSize: '0.72rem', color: isBoldRed ? '#c62828' : '#374151', fontWeight: isBoldRed ? 700 : 400, lineHeight: 1.5, marginBottom: '0.25rem' }}>{insight}</li>
                );
              })}
            </ul>
          ) : (
            <div style={{ fontSize: '0.7rem', color: TEXT_MUTED }}>Select a request to see insights.</div>
          )}
        </div>
      </div>
    );
  };

  // ─── Pane 3: Fulfillment / Actions ──────────────────────────────────────────
  const renderFulfillmentPane = () => {
    if (!detailData) return <div style={{ padding: '1rem', fontSize: '0.78rem', color: TEXT_MUTED, textAlign: 'center' }}>Select a request</div>;
    const statusColor = (STATUS_COLORS[detailData.status] || STATUS_COLORS.Requested).color;
    return (
      <div style={{ padding: '0.6rem' }}>
        {/* Editable Name */}
        {!editingName ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', marginBottom: '0.4rem' }}>
            <span style={{ fontSize: '0.82rem', fontWeight: 700, color: TEXT_DARK }}>{detailData.name}</span>
            <button onClick={() => { setEditingName(true); setEditNameValue(detailData.name); }} title="Edit name" aria-label="Edit request name" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', fontSize: '0.75rem', lineHeight: 1, color: TEXT_MUTED }}>✏️</button>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', marginBottom: '0.4rem' }}>
            <input value={editNameValue} onChange={(e) => setEditNameValue(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') handleRename(); if (e.key === 'Escape') setEditingName(false); }} autoFocus style={{ flex: 1, padding: '0.25rem 0.4rem', fontSize: '0.78rem', border: `1px solid ${PRIMARY}`, borderRadius: '4px', outline: 'none', boxSizing: 'border-box' }} />
            <button onClick={handleRename} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.8rem', color: PRIMARY }}>✓</button>
            <button onClick={() => setEditingName(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.8rem', color: TEXT_MUTED }}>✕</button>
          </div>
        )}
        <div style={{ fontSize: '0.78rem', color: TEXT_MUTED, marginBottom: '0.4rem' }}>
          Status: <strong style={{ color: statusColor }}>{detailData.status}</strong>
        </div>
        {detailData.rejectionReason && (
          <div style={{ fontSize: '0.72rem', color: '#c62828', backgroundColor: '#ffebee', padding: '0.35rem', borderRadius: '4px', marginBottom: '0.4rem' }}>
            Reason: {detailData.rejectionReason}
          </div>
        )}
        <div style={{ fontSize: '0.68rem', color: TEXT_MUTED, marginBottom: '0.6rem' }}>
          By {detailData.createdBy.fullName} &middot; {new Date(detailData.createdAt).toLocaleDateString()}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
          {detailData.status === 'Requested' && (
            <button onClick={() => handleStatusChange('InProgress')} style={{ padding: '0.4rem', fontSize: '0.75rem', fontWeight: 600, backgroundColor: '#e65100', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
              Mark In Progress
            </button>
          )}
          {(detailData.status === 'InProgress' || detailData.status === 'Requested') && (
            <>
              <button onClick={handleCreateFamilyGroup} disabled={creatingGroup || detailData.members.filter((m) => m.guestId && !m.guest?.familyAssociation).length === 0} style={{ padding: '0.4rem', fontSize: '0.75rem', fontWeight: 600, backgroundColor: '#7c3aed', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', opacity: (creatingGroup || detailData.members.filter((m) => m.guestId && !m.guest?.familyAssociation).length === 0) ? 0.5 : 1 }}>
                {creatingGroup ? 'Creating...' : '👨‍👩‍👧 Create Family Group'}
              </button>
              {detailData.members.filter((m) => m.guestId).length > 0 && detailData.members.filter((m) => m.guestId && !m.guest?.familyAssociation).length === 0 && (
                <div style={{ fontSize: '0.67rem', color: '#c62828', fontWeight: 600, padding: '0.25rem 0.4rem', backgroundColor: '#ffebee', borderRadius: '4px' }}>
                  All members already belong to a family group. No new group can be created.
                </div>
              )}
            </>
          )}
          {detailData.status !== 'Fulfilled' && detailData.status !== 'Inactive' && detailData.status !== 'Rejected' && (
            <button onClick={() => handleStatusChange('Fulfilled')} style={{ padding: '0.4rem', fontSize: '0.75rem', fontWeight: 600, backgroundColor: '#1b5e20', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
              ✅ Mark Fulfilled
            </button>
          )}
          {detailData.status === 'GroupCreated' && (
            <div style={{ fontSize: '0.68rem', color: '#2e7d32', backgroundColor: '#e8f5e9', padding: '0.35rem', borderRadius: '4px', textAlign: 'center' }}>
              Family group created
            </div>
          )}
          {/* Seat Family Group — when group exists but members not seated */}
          {detailData.members.some((m) => m.guest?.familyAssociation) && detailData.members.filter((m) => m.guest?.seat && m.guest.seat.length > 0).length === 0 && (
            <>
              <button onClick={() => setShowSeatModal(true)} disabled={seatingGroup} style={{ padding: '0.4rem', fontSize: '0.75rem', fontWeight: 600, backgroundColor: '#0277bd', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', opacity: seatingGroup ? 0.5 : 1 }}>
                {seatingGroup ? 'Seating...' : '🪑 Seat Family Group'}
              </button>
              {seatingResult && (
                <div style={{ fontSize: '0.68rem', color: '#0277bd', backgroundColor: '#e1f5fe', padding: '0.35rem', borderRadius: '4px', textAlign: 'center' }}>
                  {seatingResult}
                </div>
              )}
            </>
          )}

          {detailData.status !== 'Rejected' && detailData.status !== 'Fulfilled' && detailData.status !== 'Inactive' && (
            <div style={{ marginTop: '0.4rem', paddingTop: '0.4rem', borderTop: `1px dashed ${BORDER_LIGHT}` }}>
              <input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Rejection reason..." style={{ width: '100%', padding: '0.3rem 0.4rem', fontSize: '0.72rem', border: `1px solid ${BORDER_LIGHT}`, borderRadius: '4px', marginBottom: '0.25rem', boxSizing: 'border-box' }} />
              <button onClick={() => handleStatusChange('Rejected')} disabled={!rejectReason.trim()} style={{ padding: '0.3rem 0.5rem', fontSize: '0.72rem', fontWeight: 600, backgroundColor: '#d32f2f', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', opacity: rejectReason.trim() ? 1 : 0.5 }}>
                Reject
              </button>
            </div>
          )}
          {detailData.status !== 'Inactive' && detailData.status !== 'Fulfilled' && (
            <button onClick={handleMakeInactive} style={{ marginTop: '0.4rem', padding: '0.35rem', fontSize: '0.72rem', fontWeight: 600, backgroundColor: '#f3f4f6', color: '#6b7280', border: `1px solid ${BORDER_LIGHT}`, borderRadius: '4px', cursor: 'pointer' }}>
              Make Inactive
            </button>
          )}
          {detailData.status === 'Inactive' && (
            <div style={{ fontSize: '0.68rem', color: '#6b7280', backgroundColor: '#f3f4f6', padding: '0.35rem', borderRadius: '4px', textAlign: 'center' }}>This request is inactive</div>
          )}
          <button onClick={handleDelete} style={{ marginTop: '0.6rem', padding: '0.3rem', fontSize: '0.68rem', backgroundColor: '#fdecea', color: '#c62828', border: '1px solid #f5c6cb', borderRadius: '4px', cursor: 'pointer' }}>
            Delete Request
          </button>
          {/* Table Number Pills — when all linked guests are seated */}
          {(() => {
            const linkedMembers = detailData.members.filter((m) => m.guestId);
            const seatedMembers = linkedMembers.filter((m) => m.guest?.seat && m.guest.seat.length > 0);
            if (linkedMembers.length > 0 && seatedMembers.length === linkedMembers.length) {
              const tableNumbers = [...new Set(seatedMembers.map((m) => m.guest!.seat![0].table.tableNumber))].sort((a, b) => a - b);
              return (
                <div style={{ marginTop: '0.75rem', paddingTop: '0.6rem', borderTop: `1px solid ${BORDER_LIGHT}` }}>
                  <div style={{ fontSize: '0.65rem', fontWeight: 600, color: TEXT_MUTED, textTransform: 'uppercase', marginBottom: '0.4rem', textAlign: 'center' }}>Assigned Tables</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', justifyContent: 'center' }}>
                    {tableNumbers.map((tNum) => (
                      <div key={tNum} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '52px', height: '52px', borderRadius: '50%', backgroundColor: '#1a6b3c', color: '#fff', fontSize: '1.3rem', fontWeight: 800, boxShadow: '0 2px 8px rgba(26,107,60,0.3)' }}>
                        {tNum}
                      </div>
                    ))}
                  </div>
                </div>
              );
            }
            return null;
          })()}

          {/* Print Table Request Card button */}
          <div style={{ marginTop: '0.75rem', paddingTop: '0.6rem', borderTop: `1px solid ${BORDER_LIGHT}`, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <button
              onClick={() => handlePrintTableRequestCard(detailData)}
              style={{ width: '100%', padding: '0.5rem 0.6rem', fontSize: '0.78rem', fontWeight: 600, backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
              aria-label="Print Table Request Card"
            >
              🖨️ Print Table Request Card
            </button>
            <button
              onClick={() => handleShareToWhatsApp(detailData)}
              style={{ width: '100%', padding: '0.5rem 0.6rem', fontSize: '0.78rem', fontWeight: 600, backgroundColor: '#25D366', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
              aria-label="Share table request guest list to WhatsApp"
            >
              📱 Share to WhatsApp
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ─── Main Render ────────────────────────────────────────────────────────────
  return (
    <div>
      {error && <div style={{ color: '#d32f2f', backgroundColor: '#fdecea', padding: '0.5rem 0.75rem', borderRadius: '4px', marginBottom: '0.75rem', fontSize: '0.8rem' }}>{error}<button onClick={() => setError('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', color: '#d32f2f' }}>✕</button></div>}

      {/* Toolbar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.4rem' }}>
        <span style={{ fontSize: '0.82rem', color: TEXT_MUTED }}>{requests.length} request{requests.length !== 1 ? 's' : ''}</span>
        <button onClick={() => setShowCreate(true)} style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem', fontWeight: 600, backgroundColor: PRIMARY, color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
          + New Request
        </button>
      </div>

      {/* Mobile: pane tabs */}
      {isMobile && (
        <div style={{ display: 'flex', gap: '0', marginBottom: '0.5rem', borderBottom: `2px solid ${BORDER_LIGHT}` }}>
          {(['list', 'detail', 'actions'] as const).map((pane) => (
            <button key={pane} onClick={() => setMobilePane(pane)} style={{ flex: 1, padding: '0.4rem', fontSize: '0.72rem', fontWeight: 600, textTransform: 'capitalize', border: 'none', cursor: 'pointer', backgroundColor: mobilePane === pane ? BG_CARD : BG_SURFACE, borderBottom: mobilePane === pane ? `2px solid ${PRIMARY}` : '2px solid transparent', color: mobilePane === pane ? TEXT_DARK : TEXT_MUTED }}>
              {pane === 'list' ? 'Requests' : pane === 'detail' ? 'Members' : 'Actions'}
            </button>
          ))}
        </div>
      )}

      {/* Desktop: 3-pane grid */}
      {!isMobile && (
        <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr 200px', gap: 0, border: `1px solid ${BORDER_LIGHT}`, borderRadius: '8px', overflow: 'hidden', minHeight: '450px' }}>
          <div style={{ borderRight: `1px solid ${BORDER_LIGHT}`, overflowY: 'auto', maxHeight: '750px' }}>
            {renderRequestList()}
          </div>
          <div style={{ borderRight: `1px solid ${BORDER_LIGHT}`, display: 'flex', flexDirection: 'column', maxHeight: '750px' }}>
            <div style={{ padding: '0.5rem 0.6rem', backgroundColor: BG_SURFACE, borderBottom: `1px solid ${BORDER_LIGHT}`, fontSize: '0.72rem', fontWeight: 700, color: TEXT_MUTED, textTransform: 'uppercase', flexShrink: 0 }}>Members {detailData ? `(${detailData.members.length})` : ''}</div>
            {renderMembersPane()}
          </div>
          <div style={{ overflowY: 'auto', maxHeight: '750px' }}>
            <div style={{ padding: '0.5rem 0.6rem', backgroundColor: BG_SURFACE, borderBottom: `1px solid ${BORDER_LIGHT}`, fontSize: '0.72rem', fontWeight: 700, color: TEXT_MUTED, textTransform: 'uppercase', flexShrink: 0 }}>Fulfillment</div>
            {renderFulfillmentPane()}
          </div>
        </div>
      )}

      {/* Mobile: single pane view */}
      {isMobile && (
        <div style={{ border: `1px solid ${BORDER_LIGHT}`, borderRadius: '8px', overflow: 'hidden', minHeight: '300px' }}>
          {mobilePane === 'list' && renderRequestList()}
          {mobilePane === 'detail' && (
            <div style={{ display: 'flex', flexDirection: 'column', maxHeight: '70vh' }}>
              <div style={{ padding: '0.5rem 0.6rem', backgroundColor: BG_SURFACE, borderBottom: `1px solid ${BORDER_LIGHT}`, fontSize: '0.72rem', fontWeight: 700, color: TEXT_MUTED, textTransform: 'uppercase' }}>Members {detailData ? `(${detailData.members.length})` : ''}</div>
              {renderMembersPane()}
            </div>
          )}
          {mobilePane === 'actions' && (
            <div>
              <div style={{ padding: '0.5rem 0.6rem', backgroundColor: BG_SURFACE, borderBottom: `1px solid ${BORDER_LIGHT}`, fontSize: '0.72rem', fontWeight: 700, color: TEXT_MUTED, textTransform: 'uppercase' }}>Fulfillment</div>
              {renderFulfillmentPane()}
            </div>
          )}
        </div>
      )}

      {/* Create Request Modal */}
      {showCreate && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }} onClick={() => !saving && setShowCreate(false)}>
          <div style={{ backgroundColor: BG_CARD, borderRadius: '8px', padding: isMobile ? '1rem' : '1.5rem', width: '500px', maxWidth: '100%', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: '1rem', fontWeight: 700, color: TEXT_DARK, marginBottom: '1rem' }}>New Table Request</div>

            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: TEXT_DARK, marginBottom: '0.3rem' }}>Request Name</label>
              <input value={createName} onChange={(e) => setCreateName(e.target.value)} placeholder="e.g., Smith Family - Table Request" style={{ width: '100%', padding: '0.4rem 0.5rem', fontSize: '0.82rem', border: `1px solid ${BORDER_LIGHT}`, borderRadius: '4px', outline: 'none', boxSizing: 'border-box' }} />
            </div>

            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: TEXT_DARK, marginBottom: '0.3rem' }}>Add Guests</label>
              <input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search guest name..." style={{ width: '100%', padding: '0.4rem 0.5rem', fontSize: '0.82rem', border: `1px solid ${BORDER_LIGHT}`, borderRadius: '4px', outline: 'none', boxSizing: 'border-box' }} />
              {(searching || searchResults.length > 0) && (
                <div style={{ maxHeight: '150px', overflowY: 'auto', border: `1px solid ${BORDER_LIGHT}`, borderRadius: '4px', marginTop: '0.3rem' }}>
                  {searching && <div style={{ padding: '0.5rem', fontSize: '0.75rem', color: TEXT_MUTED, textAlign: 'center' }}>Searching...</div>}
                  {searchResults.map((g) => (
                    <div key={g.id} onClick={() => { setPendingMembers((prev) => [...prev, { guestId: g.id, displayName: g.fullName, familyGroup: g.familyAssociation }]); setSearchQuery(''); setSearchResults([]); }} style={{ padding: '0.4rem 0.6rem', fontSize: '0.78rem', cursor: 'pointer', borderBottom: `1px solid ${BORDER_LIGHT}`, display: 'flex', justifyContent: 'space-between' }}>
                      <span>{g.fullName}</span>
                      <span style={{ fontSize: '0.68rem', color: TEXT_MUTED }}>{g.familyAssociation ? `👨‍👩‍👧 ${g.familyAssociation}` : g.ticketNumber || ''}</span>
                    </div>
                  ))}
                </div>
              )}
              {searchQuery.length >= 2 && searchResults.length === 0 && !searching && (
                <button onClick={() => { setPendingMembers((prev) => [...prev, { freeformName: searchQuery.trim(), displayName: searchQuery.trim() }]); setSearchQuery(''); }} style={{ marginTop: '0.3rem', padding: '0.35rem 0.6rem', fontSize: '0.75rem', backgroundColor: '#fff3e0', color: '#e65100', border: '1px solid #fde68a', borderRadius: '4px', cursor: 'pointer' }}>
                  + Add "{searchQuery.trim()}" as new name
                </button>
              )}
            </div>

            {pendingMembers.length > 0 && (
              <div style={{ marginBottom: '0.75rem' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: TEXT_DARK, marginBottom: '0.3rem' }}>Selected ({pendingMembers.length})</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                  {pendingMembers.map((m, i) => (
                    <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.2rem', padding: '0.2rem 0.5rem', fontSize: '0.72rem', backgroundColor: m.guestId ? BG_SURFACE : '#fff3e0', border: `1px solid ${BORDER_LIGHT}`, borderRadius: '12px' }}>
                      {m.displayName}
                      {m.familyGroup && <span style={{ fontSize: '0.6rem', color: '#7c3aed' }}>({m.familyGroup})</span>}
                      {!m.guestId && <span style={{ fontSize: '0.6rem', color: '#e65100' }}>(new)</span>}
                      <button onClick={() => setPendingMembers((prev) => prev.filter((_, idx) => idx !== i))} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.8rem', lineHeight: 1, padding: '0 2px', color: TEXT_MUTED }}>✕</button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button onClick={() => { setShowCreate(false); setPendingMembers([]); setCreateName(''); }} style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem', backgroundColor: '#e0e0e0', color: '#333', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleCreate} disabled={saving || !createName.trim() || pendingMembers.length === 0} style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem', fontWeight: 600, backgroundColor: PRIMARY, color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', opacity: (!createName.trim() || pendingMembers.length === 0) ? 0.5 : 1 }}>
                {saving ? 'Creating...' : 'Create Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Configure Batch Seating Modal */}
      {showSeatModal && detailData && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }} onClick={() => !seatingGroup && setShowSeatModal(false)}>
          <div style={{ backgroundColor: BG_CARD, borderRadius: '8px', padding: isMobile ? '1rem' : '1.5rem', width: '420px', maxWidth: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: '1rem', fontWeight: 700, color: TEXT_DARK, marginBottom: '1rem' }}>Configure Batch Seating</div>

            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: TEXT_DARK, marginBottom: '0.3rem' }}>Zone Preference</label>
              <select value={seatZoneId || ''} onChange={(e) => setSeatZoneId(e.target.value || null)} style={{ width: '100%', padding: '0.4rem 0.5rem', fontSize: '0.82rem', border: `1px solid ${BORDER_LIGHT}`, borderRadius: '4px', outline: 'none', boxSizing: 'border-box' }}>
                <option value="">Any available zone</option>
                {zones.map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}
              </select>
            </div>

            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: TEXT_DARK, marginBottom: '0.3rem' }}>Assignment Rule</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                {([
                  { value: 'keep_together', label: 'Keep at same table' },
                  { value: 'allow_split', label: 'Allow splitting across adjacent tables' },
                  { value: 'start_new_table', label: 'Start new table (requires empty table with 10 seats in zone)' },
                ] as const).map((opt) => (
                  <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.78rem', color: TEXT_DARK, cursor: 'pointer' }}>
                    <input type="radio" name="seatRule" value={opt.value} checked={seatRule === opt.value} onChange={() => setSeatRule(opt.value)} style={{ margin: 0 }} />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>

            <div style={{ fontSize: '0.72rem', color: TEXT_MUTED, marginBottom: '1rem', padding: '0.4rem', backgroundColor: BG_SURFACE, borderRadius: '4px' }}>
              Seating 1 group ({detailData.members.filter((m) => m.guest?.familyAssociation).length} members)
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button onClick={() => setShowSeatModal(false)} style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem', backgroundColor: '#e0e0e0', color: '#333', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleSeatFamilyGroup} disabled={seatingGroup} style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem', fontWeight: 600, backgroundColor: '#0277bd', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', opacity: seatingGroup ? 0.5 : 1 }}>
                {seatingGroup ? 'Seating...' : 'Seat Group'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
